/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import styled from 'styled-components';
import {
    SPACING_M,
    SPACING_XXL,
    STACK_ALIGN,
    STACK_GAP,
    StackView,
    Text,
    useStateValue,
} from '@veeam/components';

import type {
    KeyboardInteractable,
    MouseInteractable,
    Styleable,
    Testable,
} from '@veeam/components';

import { StyledToggleButton, StyledToggleButtons } from '../ToggleButton';

const Wrapper = styled.div`
    display: inline-flex;
    box-sizing: border-box;
    height: ${SPACING_XXL};
    padding: 0 ${SPACING_M};
`;

interface ToggleButtonBaseProps<ValueT> {
    /**
     * Title of control
     */
    title: string;
    /**
     * Value control
     */
    value: ValueT | ValueT[];
    /**
     * Disable control
     */
    disabled?: boolean;
}

export interface ToggleButtonWithIconProps<ValueT> extends ToggleButtonBaseProps<ValueT> {
    /**
     * Path for image
     */
    icon?: string | React.ReactElement<{ error?: boolean; disabled?: boolean; }>;
}

export interface ToggleButtonWithTextProps<ValueT> extends ToggleButtonBaseProps<ValueT> {
    /**
     * Text of control
     */
    text?: string;
}

export type ToggleButtonProps<ValueT> =
    | ToggleButtonWithIconProps<ValueT>
    | ToggleButtonWithTextProps<ValueT>;

export interface PoweredBasicFilterProps<ValueT>
    extends Styleable,
        Testable,
        MouseInteractable,
        KeyboardInteractable {
    /**
     * Label of BasicFilter
     */
    label: string;
    /**
     * List of items as toggle buttons
     */
    buttons: ToggleButtonProps<ValueT>[];
    /**
     * Current date value as selected items
     */
    value?: ValueT[];
    /**
     * BasicFilter change handler
     */
    onChange?: (selectedIdItems?: ValueT[]) => void;
    /**
     * Set hide/show control of select/unselect all items
     */
    hasAllButton?: boolean;
    /**
     * Text of control of select/unselect all items
     */
    textAllButton?: string;
}

export const PoweredBasicFilter = React.forwardRef(<ValueT,>(
    props: PoweredBasicFilterProps<ValueT>,
    ref: React.RefObject<HTMLDivElement>
): React.ReactElement => {
    const {
        className,
        label,
        buttons,
        value: selectedItems = [],
        onChange,
        hasAllButton = false,
        textAllButton = 'All',

        onClick,
        onMouseDown,
        onMouseUp,
        onMouseEnter,
        onMouseLeave,
        onKeyDown,
        onKeyUp,
        onKeyPress,
        style,

        ...rest
    } = props;
    const { getValue, setValue } = useStateValue(selectedItems, [selectedItems]);
    const updateValue = (value: ValueT | ValueT[], isToggled: boolean) => {
        const currentValue = [...getValue()];
        const isIdSelected = Array.isArray(value) ? value.some(val => currentValue.includes(val)) : currentValue.includes(value);

        if (!isToggled && isIdSelected) {
            return currentValue.filter(i => Array.isArray(value) ? !value.includes(i) : i !== value);
        }

        if (isToggled && !isIdSelected) {
            Array.isArray(value) ? currentValue.push(...value) : currentValue.push(value);
        }

        return currentValue;
    };

    const handleChangeValue = (value: ValueT | ValueT[], isToggled: boolean) => {
        const newValue = updateValue(value, isToggled);

        setValue(newValue);
        onChange?.(newValue);
    };

    const handleAllChangeValue = (isToggled: boolean) => {
        if (isToggled) {
            setValue([]);
            onChange?.([]);
        }
    };
    const isAllButtonToggled = getValue().length === 0;

    return (
        <Wrapper
            {...rest}
            data-selector-id={rest['data-selector-id'] || 'basicfilter'}
            className={`vm-PoweredBasicFilter ${className}`}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            onKeyPress={onKeyPress}
            ref={ref}
            style={style}
        >
            <StackView align={STACK_ALIGN.center} gap={STACK_GAP.m}>
                <Text>
                    {`${label}:`}
                </Text>

                <StyledToggleButtons>
                    {hasAllButton && (
                        <StyledToggleButton
                            data-selector-id='basicfilter-toggle-button-all'
                            isToggled={isAllButtonToggled}
                            onChange={handleAllChangeValue}
                        >
                            {textAllButton}
                        </StyledToggleButton>
                    )}

                    {buttons.map(
                        (
                            { title, value, disabled, ...rest }: ToggleButtonProps<ValueT>,
                            idx: number,
                        ) => (
                            <StyledToggleButton
                                key={`basicfilter-toggle-button-${idx}`}
                                data-selector-id={`basicfilter-toggle-button-${idx}`}
                                icon={rest['icon']}
                                title={title}
                                isToggled={Array.isArray(value) ? value.every(val => getValue().includes(val)) : getValue().includes(value)}
                                onChange={(isToggled: boolean) =>
                                    handleChangeValue(value, isToggled)}
                                disabled={disabled}
                            >
                                {rest['text']}
                            </StyledToggleButton>
                        ),
                    )}
                </StyledToggleButtons>
            </StackView>
        </Wrapper>
    );
});
